import React from 'react';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';
import Button from '../../../components/ui/Button';
import { cn } from '../../../utils/cn';

const AccountSettingsSection = ({
  formData,
  errors,
  onFieldChange,
  onGeneratePassword
}) => {
  return (
    <div className="bg-white rounded-lg border border-border shadow-sm p-6">
      <div className="mb-6">
        <h2 className="text-lg font-semibold text-foreground mb-2">
          Configurações da conta
        </h2>
        <p className="text-sm text-muted-foreground">
          Configure as credenciais e as opções iniciais da conta
        </p>
      </div>
      <div className="space-y-6">
        {/* Password Generation Options */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-3">
            Definição de senha
          </label>
          
          <div className="space-y-3">
            {/* Auto-generate Password Option */}
            <div className="flex items-start space-x-3 p-3 rounded-lg border border-border">
              <Checkbox
                id="generatePassword"
                checked={formData?.generatePassword}
                onCheckedChange={(checked) => onFieldChange?.('generatePassword', checked)}
              />
              <div className="flex-1">
                <label htmlFor="generatePassword" className="text-sm font-medium text-foreground cursor-pointer">
                  Gerar senha segura automaticamente
                </label>
                <p className="text-xs text-muted-foreground mt-1">
                  O sistema criará uma senha forte, aleatória, automaticamente
                </p>
              </div>
            </div>

            {/* Manual Password Input */}
            {!formData?.generatePassword && (
              <div>
                <label htmlFor="temporaryPassword" className="block text-sm font-medium text-foreground mb-2">
                  Senha temporária <span className="text-red-500">*</span>
                </label>
                <div className="flex space-x-2">
                  <Input
                    id="temporaryPassword"
                    type="password"
                    placeholder="Digite a senha temporária"
                    value={formData?.temporaryPassword || ''}
                    onChange={(e) => onFieldChange?.('temporaryPassword', e?.target?.value)}
                    className={cn(
                      'flex-1',
                      errors?.temporaryPassword && 'border-red-300 focus:border-red-500 focus:ring-red-200'
                    )}
                    autoComplete="new-password"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={onGeneratePassword}
                    iconName="RefreshCw"
                    className="flex-shrink-0"
                  >
                    Gerar
                  </Button>
                </div>
                {errors?.temporaryPassword && (
                  <p className="mt-1 text-xs text-red-600">
                    {errors?.temporaryPassword}
                  </p>
                )}
                {!errors?.temporaryPassword && (
                  <p className="mt-1 text-xs text-muted-foreground">
                    A senha deve ter pelo menos 8 caracteres
                  </p>
                )}
              </div>
            )}

            {/* Generated Password Display */}
            {formData?.generatePassword && formData?.temporaryPassword && (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                <label className="block text-sm font-medium text-foreground mb-2">
                  Senha gerada
                </label>
                <div className="flex items-center space-x-2">
                  <Input
                    type="text"
                    value={formData?.temporaryPassword}
                    readOnly
                    className="flex-1 font-mono text-sm"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      navigator?.clipboard?.writeText(formData?.temporaryPassword);
                    }}
                    iconName="Copy"
                  >
                    Copiar
                  </Button>
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  Guarde esta senha em segurança - ela será necessária no primeiro acesso
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Account Preferences */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-3">
            Opções iniciais
          </label>
          
          <div className="space-y-3">
            {/* Send Invitation Email */}
            <div className="flex items-start space-x-3">
              <Checkbox
                id="sendInvitation"
                checked={formData?.sendInvitation}
                onCheckedChange={(checked) => onFieldChange?.('sendInvitation', checked)}
              />
              <div className="flex-1">
                <label htmlFor="sendInvitation" className="text-sm font-medium text-foreground cursor-pointer">
                  Enviar e-mail de convite
                </label>
                <p className="text-xs text-muted-foreground mt-1">
                  O usuário receberá as credenciais e instruções de acesso por e-mail
                </p>
              </div>
            </div>

            {/* Require Password Change */}
            <div className="flex items-start space-x-3">
              <Checkbox
                id="requirePasswordChange"
                checked={formData?.requirePasswordChange}
                onCheckedChange={(checked) => onFieldChange?.('requirePasswordChange', checked)}
              />
              <div className="flex-1">
                <label htmlFor="requirePasswordChange" className="text-sm font-medium text-foreground cursor-pointer">
                  Exigir troca de senha no primeiro acesso
                </label>
                <p className="text-xs text-muted-foreground mt-1">
                  O usuário deverá definir uma nova senha ao acessar pela primeira vez
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Security Notice */}
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
          <div className="flex items-start space-x-2">
            <svg className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
            <div className="text-sm text-amber-800">
              <p className="font-medium mb-1">Boas práticas de segurança</p>
              <ul className="list-disc list-inside space-y-0.5 text-xs">
                <li>Sempre use senhas fortes e únicas</li>
                <li>Ative a exigência de troca de senha para mais segurança</li>
                <li>Compartilhe credenciais com segurança apenas com o usuário</li>
                <li>Monitore a atividade da conta após a criação</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountSettingsSection;